import React, { useContext, useMemo, useState } from "react"
import styles from "./SingleChoice.module.sass"
import ChoiceCategoryHeader from "./ChoiceCategoryHeader"
import Chip from "./Chip"
import BigChip from "./BigChip"
import MultiChoice from "./MultiChoice"
import TradeInThemeContext from "../TradeInWizard/contexts/theme"
import { Choice, ChoiceValue } from "./carAssemblageTypes"
import { CommonChoiceProps } from "./SingleChoice"

const isSelected = (
  value: ChoiceValue,
  selected?: ChoiceListProps["selected"]
) =>
  selected === undefined || selected === null
    ? false
    : Object.keys(value).every((key) => value[key] === selected[key])

const addEmptyCategory = (choice: Choice) => ({
  ...choice,
  category: "",
})

type ChoiceListProps = {
  choices: Choice[]
  category?: string
  labels: {
    continue: string
    nestedMultiChoicesPrompt: string
  }
  otherChip: JSX.Element | null
} & CommonChoiceProps

const ChoiceList = ({
  choices,
  nestedMultiChoices,
  category,
  fullWidthOptions,
  selectItem,
  selected,
  labels,
  isSellYourCar,
  showConfirmButtonAfterSelection,
  otherChip,
  stepName,
}: ChoiceListProps) => {
  const theme = useContext(TradeInThemeContext)
  const [itemSelected, setItemSelected] = useState(
    choices.find((item) => isSelected(item.value, selected))
  )

  const showConfirmButton = useMemo(
    () => (showConfirmButtonAfterSelection ? Boolean(itemSelected) : true),
    [itemSelected, showConfirmButtonAfterSelection]
  )

  /** The confirm button is used for and set when the MultiChoice is rendered */
  const [confirmButton, setConfirmButton] = useState(
    <button
      type="button"
      disabled
      className={[
        "main-cta",
        ...(isSellYourCar
          ? ["uds-button", "primary", "full-width", "disabled"]
          : [styles.confirmButton]),
      ].join(" ")}
    >
      {labels.continue}
    </button>
  )

  /**
   * When an item is selected, either save it in the state and allow the user
   * to make further nested choices, or submit it to parent and finalize user input
   */
  const onSelectItem = (value: ChoiceValue) => {
    if (nestedMultiChoices) {
      const selectedItem = choices.find((item) => item.value === value)
      setItemSelected(selectedItem)
    } else {
      selectItem(value)
    }
  }

  /**
   * When the user is allowed to make additional (nested) choices, this function is
   * called when a confirm button is pressed
   */
  // TODO: figure out type for selectedMultiChoiceItems
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onConfirmSelection = (selectedMultiChoiceItems: any) => {
    selectItem({
      ...itemSelected!.value,
      [nestedMultiChoices!.objectName]: selectedMultiChoiceItems,
    })
  }

  return (
    <>
      <div className={styles.chipList}>
        {category && <ChoiceCategoryHeader>A-Z</ChoiceCategoryHeader>}
        {fullWidthOptions
          ? choices.map((item) => (
              <BigChip
                key={JSON.stringify(item.value)}
                label={item.label}
                value={item.value}
                description={item.description}
                shortDescription={item.shortDescription}
                onSelect={onSelectItem}
                isSelected={item === itemSelected}
                withCheckbox={isSellYourCar}
              >
                {nestedMultiChoices && (
                  <div className={styles.nestedMultiChoices}>
                    <div className={styles.nestedMultiChoicesPrompt}>
                      {labels.nestedMultiChoicesPrompt}
                    </div>
                    <MultiChoice
                      choices={nestedMultiChoices.choices.map(addEmptyCategory)}
                      theme={theme}
                      labels={labels}
                      renderAppendToStep={(confirmButton: JSX.Element) => {
                        setConfirmButton(confirmButton)
                      }}
                      doneCallback={onConfirmSelection}
                      isSellYourCar={isSellYourCar}
                      stepName={stepName}
                    />
                  </div>
                )}
              </BigChip>
            ))
          : choices.map((item) => (
              <Chip
                key={JSON.stringify(item.value)}
                label={item.label}
                value={item.value}
                onSelect={onSelectItem}
                color={typeof item.color !== "undefined" ? item.color : null}
                isSelected={isSelected(item.value, selected)}
              />
            ))}
        {otherChip}
      </div>
      {nestedMultiChoices && showConfirmButton && confirmButton}
    </>
  )
}

export default ChoiceList
